import React, { useState, useEffect, useRef, useContext, useCallback } from 'react'
import {
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Button,
} from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthContext } from '../../../contexts/AuthContext'
import PageHeader from '../../../components/Common/PageHeader'
import axios from '../../../api/axios'
// 导入新的 hooks 和工具函数
import { useNotifications } from './hooks/useNotifications'
import { useNotebookUpload } from './hooks/useNotebookUpload'
import { getFileType } from '../../../utils/fileUtils'
// 导入组件
import SourceAndStatusPanel from './components/SourceAndStatusPanel'
import PreviewPanel from './components/PreviewPanel'
import { getMockState, getMockStateOutlineReady, getMockStateStep1, getMockStateStep2, getMockStateStep4, getMockStateStep5 } from './mockUploadData'

const UploadNotebookPage = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, selectedCourse } = useContext(AuthContext)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const [sources, setSources] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [messages, setMessages] = useState([])
  const [notebookData, setNotebookData] = useState(null) // { outline: [], sections: [], description: '' }
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedCourseCode, setSelectedCourseCode] = useState('')
  const [exerciseTypes] = useState({
    multiple_choice: true, // 选择题
    proof: false, // 证明题
    code: true, // 代码题
    code_tracing: true, // 代码追踪题
    short_answer: false, // 简答题
  })
  const [contentStyle, setContentStyle] = useState('auto') // 'casual' | 'professional' | 'auto'
  const [generateExercises] = useState(true) // 上传 JSON 时是否一并生成练习题
  const [generateInlineExamples, setGenerateInlineExamples] = useState(true) // 生成原文时是否包含笔记自带的教学例子（概念块内例子与独立例子）
  const [inlineExampleTypes, setInlineExampleTypes] = useState({
    multiple_choice: true,
    proof: false,
    code: true,
    code_tracing: true,
    short_answer: true,
  }) // 笔记中的例子（概念块内、独立例子）允许的题型；仅当 generateInlineExamples 为 true 时生效
  const [generationProgress, setGenerationProgress] = useState(null) // 生成进度 { stage, data }
  const [isAdjustingOutline, setIsAdjustingOutline] = useState(false) // 正在根据对话调整大纲
  const [selectedOutlineForPreview, setSelectedOutlineForPreview] = useState(null) // 左侧点击大纲项时，右侧预览展开该项
  const [selectedSectionForPreview, setSelectedSectionForPreview] = useState(null) // 左侧点击章节时，右侧预览展开该章节

  const { notifications, addNotification, removeNotification } = useNotifications()

  // 本页禁止整页滚动，仅各栏内部滚动；入场时滚回顶部，离开页面时恢复
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    const prevBodyHeight = body.style.height
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    body.style.height = '100%'
    // 避免从其他页带来滚动位置，导致本页内容在视口上方（top 为负）
    window.scrollTo(0, 0)
    if (html.scrollTop) html.scrollTop = 0
    if (body.scrollTop) body.scrollTop = 0
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
      body.style.height = prevBodyHeight
    }
  }, [])

  const { uploadResult: uploadResultFromHook, handleUploadNotebook: handleUploadNotebookFromHook, handleConfirmGenerate: handleConfirmGenerateFromHook } = useNotebookUpload({
    sources,
    selectedCourseCode,
    exerciseTypes,
    contentStyle,
    generateExercises,
    generateInlineExamples,
    inlineExampleTypes,
    setMessages,
    setIsUploading,
    setUploadProgress,
    setNotebookData,
    setGenerationProgress,
    setActiveTab,
    addNotification,
  })

  // Initialize course code from selected course
  useEffect(() => {
    if (selectedCourse?.code) {
      setSelectedCourseCode(selectedCourse.code.toUpperCase())
    }
  }, [selectedCourse])

  // URL 参数 ?mock=1|outline|step1|step2|step4|step5 时自动加载 Mock 数据
  useEffect(() => {
    const mockParam = searchParams.get('mock')
    const getMock = {
      '1': getMockState,
      outline: getMockStateOutlineReady,
      step1: getMockStateStep1,
      step2: getMockStateStep2,
      step4: getMockStateStep4,
      step5: getMockStateStep5,
    }[mockParam]
    if (getMock) {
      const mock = getMock()
      setSources(mock.sources)
      setMessages(mock.messages)
      setNotebookData(mock.notebookData)
      setGenerationProgress(mock.generationProgress)
      setSelectedCourseCode((c) => mock.selectedCourseCode || c)
      setUploadProgress(mock.uploadProgress ?? 0)
      setIsUploading(mock.isUploading ?? false)
      setSelectedOutlineForPreview(null)
      setSelectedSectionForPreview(null)
    }
  }, [searchParams])


  const handleAddSource = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleAddUrl = () => {
    handleCloseMenu()
    const url = prompt(t('uploadNotebook.enterUrl') || '请输入网页 URL:')
    if (url) {
      setSources([...sources, { id: Date.now(), type: 'url', name: url, url }])
    }
  }

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files)
    files.forEach((file) => {
      const fileType = getFileType(file.name)
      setSources((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          type: fileType,
          name: file.name,
          file,
        },
      ])
    })
    event.target.value = ''
    handleCloseMenu()
  }

  const handleRemoveSource = (sourceId) => {
    setSources((prev) => prev.filter((source) => source.id !== sourceId))
  }

  const handleConfirmGenerate = async () => {
    return handleConfirmGenerateFromHook(notebookData)
  }

  // 使用 hook 中的上传函数
  const handleUploadNotebook = handleUploadNotebookFromHook

  // 在状态栏用自然语言调整大纲
  const handleAdjustOutline = useCallback(async (userMessage) => {
    const msg = (userMessage || '').trim()
    if (!msg) return
    if (!notebookData?.notebookId) {
      setMessages((prev) => [...prev, { type: 'error', content: '暂无笔记本，请先生成大纲' }])
      return
    }
    setMessages((prev) => [...prev, { type: 'user', content: msg }])
    setIsAdjustingOutline(true)
    try {
      const res = await axios.post('/notes/adjust-outline', {
        notebookId: notebookData.notebookId,
        userMessage: msg,
      }, { timeout: 90 * 1000 })
      const { outline: newOutline, fullOutline: newFullOutline } = res.data
      setNotebookData((prev) => ({
        ...prev,
        outline: newOutline || prev.outline,
        fullOutline: newFullOutline || prev.fullOutline,
      }))
      setMessages((prev) => [
        ...prev,
        { type: 'system', content: res.data.message || '大纲已根据你的要求更新，请查看预览。' },
      ])
    } catch (err) {
      const detail = err.response?.data?.details || err.response?.data?.error || err.message
      setMessages((prev) => [...prev, { type: 'error', content: `调整失败：${detail}` }])
    } finally {
      setIsAdjustingOutline(false)
    }
  }, [notebookData?.notebookId, setMessages, setNotebookData])


  const messagesEndRef = useRef(null)
  // 不再在 messages 变化时自动滚到底部，避免把内容顶到视口上方且滑不上去；顶部由 StatusPanel 在挂载/阶段变化时滚回

  // Check if user is teacher
  if (user?.role !== 'teacher') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error">{t('uploadNotebook.teacherOnly') || '此功能仅限教师使用'}</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: '100%',
        pb: 0,
        px: { xs: 1, sm: 1.5, md: 2 },
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.background.default),
      }}
    >
      <PageHeader
        title={t('uploadNotebook.upload') || '上传笔记'}
        backPath="/notebooks"
        actions={
          process.env.NODE_ENV === 'development' ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => { const m = getMockStateStep1(); setSources(m.sources); setMessages(m.messages); setNotebookData(m.notebookData); setGenerationProgress(m.generationProgress); setSelectedCourseCode(m.selectedCourseCode || selectedCourseCode); setUploadProgress(m.uploadProgress ?? 0); setIsUploading(m.isUploading ?? false); setSelectedOutlineForPreview(null); setSelectedSectionForPreview(null) }}>Mock 步骤1</Button>
              <Button size="small" variant="outlined" onClick={() => { const m = getMockStateStep2(); setSources(m.sources); setMessages(m.messages); setNotebookData(m.notebookData); setGenerationProgress(m.generationProgress); setSelectedCourseCode(m.selectedCourseCode || selectedCourseCode); setUploadProgress(m.uploadProgress ?? 0); setIsUploading(m.isUploading ?? false); setSelectedOutlineForPreview(null); setSelectedSectionForPreview(null) }}>Mock 步骤2</Button>
              <Button size="small" variant="outlined" onClick={() => { const m = getMockStateStep4(); setSources(m.sources); setMessages(m.messages); setNotebookData(m.notebookData); setGenerationProgress(m.generationProgress); setSelectedCourseCode(m.selectedCourseCode || selectedCourseCode); setUploadProgress(m.uploadProgress ?? 0); setIsUploading(m.isUploading ?? false); setSelectedOutlineForPreview(null); setSelectedSectionForPreview(null) }}>Mock 步骤4</Button>
              <Button size="small" variant="outlined" onClick={() => { const m = getMockStateStep5(); setSources(m.sources); setMessages(m.messages); setNotebookData(m.notebookData); setGenerationProgress(m.generationProgress); setSelectedCourseCode(m.selectedCourseCode || selectedCourseCode); setUploadProgress(m.uploadProgress ?? 0); setIsUploading(m.isUploading ?? false); setSelectedOutlineForPreview(null); setSelectedSectionForPreview(null) }}>Mock 步骤5</Button>
            </Box>
          ) : null
        }
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mt: 2,
          bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.background.default),
        }}
      >
      {isSmallScreen ? (
        // Mobile/Small Screen: Tabs Layout（左：源文件+状态合并 | 右：预览）
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2, flexShrink: 0 }}
          >
            <Tab label={t('uploadNotebook.sources') || '源文件'} />
            <Tab label={t('uploadNotebook.preview') || '预览'} />
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {activeTab === 0 && (
              <SourceAndStatusPanel
                sources={sources}
                selectedCourseCode={selectedCourseCode}
                contentStyle={contentStyle}
                onAddSource={handleAddSource}
                onRemoveSource={handleRemoveSource}
                onFileUpload={handleFileUpload}
                onAddUrl={handleAddUrl}
                onCloseMenu={handleCloseMenu}
                anchorEl={anchorEl}
                onContentStyleChange={(value) => setContentStyle(value)}
                messages={messages}
                isUploading={isUploading}
                notebookData={notebookData}
                onConfirmGenerate={handleConfirmGenerate}
                onGenerateNotebook={handleUploadNotebook}
                onAdjustOutline={handleAdjustOutline}
                isAdjustingOutline={isAdjustingOutline}
                generationProgress={generationProgress}
                uploadProgress={uploadProgress}
                uploadResult={uploadResultFromHook}
                messagesEndRef={messagesEndRef}
                navigate={navigate}
                generateInlineExamples={generateInlineExamples}
                onGenerateInlineExamplesChange={setGenerateInlineExamples}
                inlineExampleTypes={inlineExampleTypes}
                onInlineExampleTypesChange={setInlineExampleTypes}
                onOutlineItemClick={(item) => { setSelectedOutlineForPreview(item); if (isSmallScreen) setActiveTab(1) }}
                onSectionClick={(section, outlineItem) => { setSelectedSectionForPreview({ section, outlineItem }); if (isSmallScreen) setActiveTab(1) }}
              />
            )}
            {activeTab === 1 && (
              <PreviewPanel
                notebookData={notebookData}
                generationProgress={generationProgress}
                onConfirmGenerate={handleConfirmGenerate}
                isUploading={isUploading}
                selectedOutlineForPreview={selectedOutlineForPreview}
                selectedSectionForPreview={selectedSectionForPreview}
              />
            )}
          </Box>
        </Box>
      ) : (
        // Desktop: 两栏布局（左：源文件+状态合并 | 右：预览）
        <Grid container spacing={3} sx={{ flex: 1, flexWrap: 'nowrap', mb: { xs: 2, sm: 2.5 }, minHeight: 0, overflow: 'hidden', alignContent: 'stretch', width: '100%' }}>
          <Grid item xs={5} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', minWidth: 0 }}>
            <SourceAndStatusPanel
              sources={sources}
              selectedCourseCode={selectedCourseCode}
              contentStyle={contentStyle}
              onAddSource={handleAddSource}
              onRemoveSource={handleRemoveSource}
              onFileUpload={handleFileUpload}
              onAddUrl={handleAddUrl}
              onCloseMenu={handleCloseMenu}
              anchorEl={anchorEl}
              onContentStyleChange={(value) => setContentStyle(value)}
              messages={messages}
              isUploading={isUploading}
              notebookData={notebookData}
              onConfirmGenerate={handleConfirmGenerate}
              onGenerateNotebook={handleUploadNotebook}
              onAdjustOutline={handleAdjustOutline}
              isAdjustingOutline={isAdjustingOutline}
              generationProgress={generationProgress}
              uploadProgress={uploadProgress}
              uploadResult={uploadResultFromHook}
              messagesEndRef={messagesEndRef}
              navigate={navigate}
              generateInlineExamples={generateInlineExamples}
              onGenerateInlineExamplesChange={setGenerateInlineExamples}
              inlineExampleTypes={inlineExampleTypes}
              onInlineExampleTypesChange={setInlineExampleTypes}
              onOutlineItemClick={(item) => { setSelectedOutlineForPreview(item); if (isSmallScreen) setActiveTab(1) }}
              onSectionClick={(section, outlineItem) => { setSelectedSectionForPreview({ section, outlineItem }); if (isSmallScreen) setActiveTab(1) }}
            />
          </Grid>
          <Grid item xs={7} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
            <PreviewPanel
              notebookData={notebookData}
              generationProgress={generationProgress}
              onConfirmGenerate={handleConfirmGenerate}
              isUploading={isUploading}
              selectedOutlineForPreview={selectedOutlineForPreview}
              selectedSectionForPreview={selectedSectionForPreview}
            />
          </Grid>
        </Grid>
      )}
      
      {/* 通知中心 */}
      <Box
        sx={{
          position: 'fixed',
          top: 80,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400,
        }}
      >
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open={true}
            autoHideDuration={notification.duration || 6000}
            onClose={() => removeNotification(notification.id)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              severity={notification.type || 'info'}
              onClose={() => removeNotification(notification.id)}
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
      </Box>
    </Box>
  )
}

export default UploadNotebookPage

